import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  const categories = [
    { id: "all", label: "All Projects" },
    { id: "kitchens", label: "Kitchens" },
    { id: "bathrooms", label: "Bathrooms" },
    { id: "windows", label: "Windows" },
    { id: "doors", label: "Doors" },
    { id: "new-construction", label: "New Construction" }
  ];

  const filteredProjects = filter === "all" 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gallery-page">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-orange-400 font-bold tracking-wider text-sm">OUR WORK</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mt-4 mb-6 font-['Chivo']">
            Project Gallery
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Browse our completed projects and see the quality craftsmanship 
            we bring to every home in Lagrange and surrounding areas.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-200 bg-white sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3 justify-center" data-testid="gallery-filters">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={filter === cat.id ? "default" : "outline"}
                className={`rounded-sm font-bold ${
                  filter === cat.id 
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white" 
                    : "border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-600"
                }`}
                onClick={() => setFilter(cat.id)}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, idx) => (
              <div 
                key={project.id || idx}
                className="gallery-card group relative bg-white rounded-sm overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setSelectedProject(project)}
                data-testid={`project-card-${idx}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover gallery-image"
                  />
                </div>
                <div className="gallery-overlay absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 flex flex-col justify-end p-6">
                  <span className="text-orange-400 text-sm font-bold uppercase tracking-wider mb-2">
                    {project.category}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-2 font-['Chivo']">
                    {project.title}
                  </h3>
                  <div className="flex items-center gap-4 text-slate-300 text-sm">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {project.completed_date}
                    </span>
                  </div>
                </div>
                <div className="p-6 border-t border-slate-100">
                  <span className="text-orange-600 text-sm font-bold uppercase tracking-wider">
                    {project.category}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mt-1 font-['Chivo']">
                    {project.title}
                  </h3>
                  <p className="text-slate-500 text-sm mt-2 line-clamp-2">
                    {project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="sm:max-w-3xl">
          {selectedProject && (
            <>
              <div className="aspect-video overflow-hidden rounded-sm mb-6">
                <img
                  src={selectedProject.image_url}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <DialogHeader>
                <span className="text-orange-600 text-sm font-bold uppercase tracking-wider">
                  {selectedProject.category}
                </span>
                <DialogTitle className="text-2xl font-bold font-['Chivo']">
                  {selectedProject.title}
                </DialogTitle>
              </DialogHeader>
              <p className="text-slate-600 my-4">{selectedProject.description}</p>
              <div className="flex items-center gap-4 text-slate-500 text-sm mb-6">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {selectedProject.location}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {selectedProject.completed_date}
                </span>
              </div>
              <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-sm font-bold py-6"
                    data-testid="project-quote-btn"
                  >
                    Get a Similar Project Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold font-['Chivo']">
                      Request a Free Quote
                    </DialogTitle>
                  </DialogHeader>
                  <QuoteForm onSuccess={() => {
                    setQuoteDialogOpen(false);
                    setSelectedProject(null);
                  }} />
                </DialogContent>
              </Dialog>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-red-500">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Chivo']">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Let us bring your vision to life. Get a free quote today!
          </p>
          <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg"
                className="bg-white text-orange-600 hover:bg-slate-100 rounded-sm font-bold px-8 py-6 text-lg"
                data-testid="gallery-cta-btn"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
